import { useCallback, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useWatchComments,
  useWorkspaceWatchData,
  type WatchComment,
  type WatchVersion,
} from "@/hooks/use-watch";
import { DualPlayer, type DualPlayerHandle } from "@/components/watch/dual-player";
import { RightPanel } from "@/components/watch/right-panel";
import { AnnotationProvider } from "@/components/watch/annotation-context";
import { Button } from "@/components/ui/button";

/**
 * ComparePage (2026-07-18) — side-by-side compare of two arbitrary video
 * assets. Entered from the SelectionActionBar's Compare button when exactly
 * two videos are selected.
 *
 * Routes:
 *   /app/compare/:aId/:bId
 *   /admin/compare/:aId/:bId
 *
 * Same layout-less shell pattern as WatchPage — sits directly under the auth
 * gate, owns the full viewport (`h-screen`). Rail is rendered by neither this
 * page nor a layout — compare is a one-off "focused" view. Back button in the
 * top bar returns to whichever page the user came from (falls back to the
 * project list if no history is available).
 *
 * Right panel: reuses the watch page's RightPanel. Comments switch between
 * the two assets via a Left/Right side toggle in the panel header. Both sides
 * fetch their own comments up front; the toggle swaps which set is rendered.
 */
export default function ComparePage() {
  const { aId, bId } = useParams<{ aId: string; bId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin/");
  const projectsHref = isAdmin ? "/admin/projects" : "/app/projects";

  const leftQuery = useWorkspaceWatchData(aId);
  const rightQuery = useWorkspaceWatchData(bId);

  // Comment fetches — one per side. Both start immediately so the toggle is
  // instant. Keys are per-videoId, so cache reuse across compare + watch is
  // free.
  const { data: leftComments = [] } = useWatchComments("workspace", aId);
  const { data: rightComments = [] } = useWatchComments("workspace", bId);

  const playerRef = useRef<DualPlayerHandle>(null);
  const [activeSide, setActiveSide] = useState<"left" | "right">("left");

  // Selected version per side. When the passed id is a root, we render the
  // latest version by default (matches the watch page's behavior). When it's
  // a specific version id in a stack, we render exactly that.
  const leftVersion = pickVersionForId(aId, leftQuery.data?.versions);
  const rightVersion = pickVersionForId(bId, rightQuery.data?.versions);

  const activeComments = activeSide === "left" ? leftComments : rightComments;
  const activeVersion = activeSide === "left" ? leftVersion : rightVersion;
  const activeWatchKey = activeSide === "left" ? aId : bId;

  // Seek/getCurrentTime for the right panel — both target the active side.
  const seek = useCallback((s: number) => {
    playerRef.current?.seek(s);
    playerRef.current?.pause();
  }, []);
  const getCurrentTime = useCallback(
    () => playerRef.current?.getCurrentTime(activeSide) ?? 0,
    [activeSide],
  );

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const handleSelectComment = useCallback((id: string) => {
    setSelectedCommentId(id);
    playerRef.current?.pause();
  }, []);

  // Loading + error handling — need BOTH sides before we can render dual player.
  const anyLoading = leftQuery.isLoading || rightQuery.isLoading;
  const anyError = leftQuery.isError || rightQuery.isError;

  if (anyLoading) {
    return (
      <div className="grid h-screen place-items-center bg-background">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading compare…
        </div>
      </div>
    );
  }
  if (anyError || !leftVersion || !rightVersion) {
    const msg =
      (leftQuery.error as Error)?.message ||
      (rightQuery.error as Error)?.message ||
      "Couldn't load one of the assets to compare.";
    return (
      <div className="grid h-screen place-items-center bg-background p-6 text-center">
        <div className="max-w-md space-y-3">
          <div className="text-lg font-semibold text-foreground">Compare unavailable</div>
          <p className="text-sm text-muted-foreground">{msg}</p>
          <Link to={projectsHref}>
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4" />
              Back to Projects
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Two-panel layout same as WatchShell — top bar + gap-px seams.
  // Mobile: stack (dual player h-[45vh] over flex-1 panel). Desktop: side-by-side.
  const topBarWrap = "overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-background";
  const videoWrap = "flex h-[45vh] overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-black md:h-auto md:flex-1";
  const rightPanelWrap = "flex min-h-0 flex-1 flex-col overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-background md:w-80 md:flex-none lg:w-96";

  return (
    <AnnotationProvider>
      <div className="flex h-screen h-[100dvh] bg-sidebar text-foreground">
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          {/* Top bar — Back button + compare title */}
          <div className={topBarWrap}>
            <header className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Back"
                className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0 flex-1 text-sm">
                <span className="font-medium text-foreground">Compare Assets</span>
                <span className="mx-2 text-muted-foreground">•</span>
                <span className="truncate text-muted-foreground">
                  {leftVersion.title || "Untitled"}{" "}
                  <span className="text-muted-foreground/60">vs</span>{" "}
                  {rightVersion.title || "Untitled"}
                </span>
              </div>
            </header>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-px md:flex-row">
            {/* Dual player — full parity with single-player bottom bar
                (2026-07-18): play/loop/speed/volume/timecode/anchor/settings/
                fullscreen. Per-side comments feed the anchor + annotation
                overlays; active side receives new pins. Closing one side
                drops the compare view and lands on the OTHER side's regular
                watch page. */}
            <div className={videoWrap}>
              <DualPlayer
                ref={playerRef}
                leftSrc={leftVersion.cdnUrl ?? null}
                rightSrc={rightVersion.cdnUrl ?? null}
                leftTitle={leftVersion.title || "Untitled"}
                rightTitle={rightVersion.title || "Untitled"}
                leftComments={leftComments}
                rightComments={rightComments}
                leftVersionId={leftVersion.id}
                rightVersionId={rightVersion.id}
                leftWatchKey={aId}
                rightWatchKey={bId}
                onActiveSideChange={setActiveSide}
                onCloseSide={(side) => {
                  // Keep the OPPOSITE side alive as a single-player view.
                  const remaining = side === "left" ? bId : aId;
                  if (!remaining) return;
                  const base = isAdmin ? "/admin/watch" : "/app/watch";
                  navigate(`${base}/${remaining}`);
                }}
              />
            </div>

            {/* Right panel with side toggle in its own thin header above the
                Comments/Fields tabs. Reuses RightPanel unchanged — the toggle
                just swaps which side's data feeds in. */}
            <aside className={rightPanelWrap}>
              <SidePicker
                active={activeSide}
                onChange={setActiveSide}
                leftLabel={leftVersion.title || "Left"}
                rightLabel={rightVersion.title || "Right"}
                leftCount={leftComments.length}
                rightCount={rightComments.length}
              />
              <div className="flex min-h-0 flex-1 flex-col">
                <RightPanel
                  mode="workspace"
                  currentVersion={activeVersion}
                  comments={activeComments}
                  watchKey={activeWatchKey}
                  onSeek={seek}
                  getCurrentTime={getCurrentTime}
                  selectedCommentId={selectedCommentId}
                  onSelectComment={handleSelectComment}
                  assetType={activeVersion?.assetType}
                />
              </div>
            </aside>
          </div>
        </div>
      </div>
    </AnnotationProvider>
  );
}

// ── Side picker (Comment feed toggle) ────────────────────────────────────────

function SidePicker({
  active,
  onChange,
  leftLabel,
  rightLabel,
  leftCount,
  rightCount,
}: {
  active: "left" | "right";
  onChange: (side: "left" | "right") => void;
  leftLabel: string;
  rightLabel: string;
  leftCount: number;
  rightCount: number;
}) {
  return (
    <div className="shrink-0 border-b border-border p-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <MessageSquare className="h-3 w-3" /> Viewing comments for
      </div>
      <div className="flex items-center gap-0.5 rounded-lg bg-secondary/60 p-0.5">
        <SideBtn
          active={active === "left"}
          onClick={() => onChange("left")}
          label={leftLabel}
          count={leftCount}
        />
        <SideBtn
          active={active === "right"}
          onClick={() => onChange("right")}
          label={rightLabel}
          count={rightCount}
        />
      </div>
    </div>
  );
}

function SideBtn({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={label}
      className={cn(
        "flex-1 truncate rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      <span className="truncate">{label}</span>
      <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-px text-[10px] tabular-nums text-primary">
        {count}
      </span>
    </button>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Given the id we routed in with (either a root or a specific version) and
 * the stack we fetched, pick the version to render. If the id matches a
 * specific version, render that; otherwise fall back to the LATEST version
 * (matches watch.tsx's pickLatest behavior).
 */
function pickVersionForId(
  id: string | undefined,
  versions: WatchVersion[] | undefined,
): WatchVersion | null {
  if (!id || !versions?.length) return null;
  const exact = versions.find((v) => v.id === id);
  if (exact) return exact;
  return [...versions].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0))[0] ?? null;
}

// Ensure WatchComment import stays "used" for downstream type consumers if we
// add per-side sort in the future. Keeping this line lightweight avoids a
// stale-import churn PR the next time we extend this file.
export type _CompareCommentRef = WatchComment;
